import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useSignals } from "@preact/signals-react/runtime";
import { useQueryClient } from "@tanstack/react-query";
import cx from "clsx";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, } from "react";
import Button from "@ingka/button";
import Hyperlink from "@ingka/hyperlink";
import SSRIcon from "@ingka/ssr-icon";
import ChevronDownIcon from "@ingka/ssr-icon/paths/chevron-down-small";
import ChevronRightIcon from "@ingka/ssr-icon/paths/chevron-right-small";
import { ORG_NODE_CHANGED_EVENT, ORG_NODE_STORAGE_KEY, isAncestorOrgNodeKey, parseOrgNodeKey, } from "@pdpp/lib-planta";
import { PopOver, useBoolean } from "@pdpp/lib-react";
import { prefetchDrivers } from "../../api/driversApi";
import { useGetOrgStructure } from "../../api/orgApi";
import { buildOrgNodeKey, buildOrgTree, getBreadCrumbs, getCountryDisplayName, getNodeDisplayLabel, getParentUnit, resolveOrgNodeKeyToNode, toForecastSelection, } from "../../handlers/orgSelectHandler";
import { setShareableSetting } from "../../shared/services/transforms";
import { getLastReceivedExternalOrgNode, selectedOrgNode$, setOrgSelectDispatching, } from "../../state/orgSelect";
import { selectedUnitMeta$ } from "../../state/unitMeta";
import "./OrgSelect.css";
const BreadcrumbButton = ({ node, onClick, }) => (_jsx(Hyperlink, { className: "orgSelect-breadcrumbButton", "data-type": node.type, onClick: (e) => {
        e.preventDefault();
        onClick();
    }, url: "#", button: true, subtle: true, disabled: node.type === "country", children: getNodeDisplayLabel(node) }));
const OrgItem = ({ node, isActive, onClick }) => (_jsxs("div", { className: cx("orgSelect-item", { active: isActive }), onClick: onClick, children: [getNodeDisplayLabel(node), isActive && _jsx(SSRIcon, { paths: ChevronRightIcon })] }));
const Breadcrumbs = ({ nodes, onClick }) => (_jsx(_Fragment, { children: nodes.map((node) => (_jsxs(Fragment, { children: [_jsx(BreadcrumbButton, { node: node, onClick: () => onClick(node.id) }), _jsx(SSRIcon, { className: "orgSelect-breadcrumbChevron", paths: ChevronRightIcon })] }, node.id))) }));
export const OrgSelect = ({ forecastApi, countryCode, onSelectionChange, }) => {
    useSignals();
    const selectedId = selectedOrgNode$.value;
    const queryClient = useQueryClient();
    const mountedRef = useRef(true);
    const selectedCountry = countryCode ?? "";
    const { data } = useGetOrgStructure(forecastApi, selectedCountry, undefined, !!selectedCountry);
    const [orgNodes, setOrgNodes] = useState([]);
    useEffect(() => {
        if (!data) {
            setOrgNodes([]);
            return;
        }
        const payload = data;
        const id = setTimeout(() => {
            setOrgNodes(buildOrgTree(payload));
        }, 0);
        return () => clearTimeout(id);
    }, [data]);
    const resolvedSelectedNode = useMemo(() => {
        if (!orgNodes.length || selectedId == null || selectedId === "") {
            return undefined;
        }
        const exactMatch = orgNodes.find((n) => n.id === selectedId);
        if (exactMatch) {
            return exactMatch;
        }
        const rawIdMatch = orgNodes.find((n) => n.type === "unit" && n.rawId === selectedId) ??
            orgNodes.find((n) => n.type === "division" && n.rawId === selectedId) ??
            orgNodes.find((n) => n.type === "department" && n.rawId === selectedId);
        if (rawIdMatch) {
            return rawIdMatch;
        }
        const key = parseOrgNodeKey(selectedId);
        if (key === undefined) {
            return (orgNodes.find((n) => n.type === "division" && n.extId === selectedId) ??
                orgNodes.find((n) => n.type === "department" && n.extId === selectedId));
        }
        return resolveOrgNodeKeyToNode(key, orgNodes);
    }, [orgNodes, selectedId]);
    useEffect(() => {
        mountedRef.current = true;
        if (!orgNodes.length || selectedId == null || selectedId === "")
            return;
        if (orgNodes.some((node) => node.id === selectedId))
            return;
        if (resolvedSelectedNode) {
            queueMicrotask(() => {
                if (mountedRef.current === false)
                    return;
                selectedOrgNode$.value = resolvedSelectedNode.id;
            });
        }
        return () => {
            mountedRef.current = false;
        };
    }, [orgNodes, resolvedSelectedNode, selectedId]);
    const lastDispatchedOrgNodeRef = useRef(null);
    const privateFallbackIdRef = useRef(null);
    useEffect(() => {
        if (!resolvedSelectedNode)
            return;
        const orgNodeValue = buildOrgNodeKey(resolvedSelectedNode, orgNodes);
        if (orgNodeValue === undefined)
            return;
        if (orgNodeValue === lastDispatchedOrgNodeRef.current)
            return;
        const receivedValue = getLastReceivedExternalOrgNode();
        if (orgNodeValue === receivedValue) {
            lastDispatchedOrgNodeRef.current = orgNodeValue;
            return;
        }
        const candidateKey = parseOrgNodeKey(orgNodeValue);
        const receivedKey = parseOrgNodeKey(receivedValue);
        if (candidateKey &&
            Boolean(isAncestorOrgNodeKey(candidateKey, receivedKey))) {
            lastDispatchedOrgNodeRef.current = orgNodeValue;
            return;
        }
        if (privateFallbackIdRef.current === resolvedSelectedNode.id) {
            lastDispatchedOrgNodeRef.current = orgNodeValue;
            return;
        }
        lastDispatchedOrgNodeRef.current = orgNodeValue;
        setShareableSetting(ORG_NODE_STORAGE_KEY, orgNodeValue);
        setOrgSelectDispatching(true);
        window.dispatchEvent(new CustomEvent(ORG_NODE_CHANGED_EVENT, {
            detail: { orgNode: orgNodeValue },
        }));
        setOrgSelectDispatching(false);
    }, [resolvedSelectedNode, orgNodes]);
    useEffect(() => {
        const selectedNode = resolvedSelectedNode;
        if (selectedNode?.type === "unit" &&
            selectedNode.countryCode != null &&
            selectedNode.countryCode !== "") {
            prefetchDrivers(queryClient, forecastApi, {
                countryCode: selectedNode.countryCode,
                unitId: selectedNode.rawId,
                divisionId: "",
                hasForecast: true,
            });
        }
    }, [forecastApi, queryClient, resolvedSelectedNode]);
    useEffect(() => {
        mountedRef.current = true;
        if (!orgNodes.length)
            return;
        if (selectedId != null &&
            selectedId !== "" &&
            resolvedSelectedNode != null) {
            return;
        }
        const nodeExists = resolvedSelectedNode != null;
        if (!nodeExists) {
            const defaultNode = orgNodes.find((n) => n.type === "unit");
            if (defaultNode) {
                privateFallbackIdRef.current = defaultNode.id;
                queueMicrotask(() => {
                    if (mountedRef.current === false)
                        return;
                    selectedOrgNode$.value = defaultNode.id;
                });
            }
        }
        return () => {
            mountedRef.current = false;
        };
    }, [orgNodes, resolvedSelectedNode, selectedId]);
    const onSelectionChangeRef = useRef(onSelectionChange);
    onSelectionChangeRef.current = onSelectionChange;
    useEffect(() => {
        if (!orgNodes.length)
            return;
        const selectedNode = resolvedSelectedNode;
        const unitNode = getParentUnit(selectedNode, orgNodes);
        if (unitNode?.extId != null &&
            unitNode.extId !== "" &&
            unitNode.extType != null &&
            unitNode.extType !== "" &&
            unitNode.countryCode != null &&
            unitNode.countryCode !== "") {
            selectedUnitMeta$.value = {
                extId: unitNode.extId,
                extType: unitNode.extType,
                countryCode: unitNode.countryCode,
                name: unitNode.name,
            };
        }
        else {
            selectedUnitMeta$.value = undefined;
        }
        if (!selectedNode) {
            onSelectionChangeRef.current({ countryCode: selectedCountry });
            return;
        }
        onSelectionChangeRef.current({
            countryCode: selectedCountry,
            ...toForecastSelection(selectedNode, orgNodes),
        });
    }, [resolvedSelectedNode, orgNodes, selectedCountry]);
    const [isPopoverShown, , hidePopover, togglePopover] = useBoolean();
    const [anchorEl, setAnchorEl] = useState(null);
    const selectNode = useCallback((id) => {
        privateFallbackIdRef.current = null;
        selectedOrgNode$.value = id;
    }, []);
    const fullBreadCrumbs = useMemo(() => {
        const countryNode = {
            id: `country-${selectedCountry}`,
            name: getCountryDisplayName(selectedCountry, navigator.language),
            type: "country",
        };
        if (!orgNodes.length || resolvedSelectedNode == null)
            return [countryNode];
        const rawCrumbs = getBreadCrumbs(orgNodes, resolvedSelectedNode.id);
        if (!rawCrumbs.length)
            return [countryNode];
        return [countryNode, ...rawCrumbs];
    }, [resolvedSelectedNode, orgNodes, selectedCountry]);
    const buttonBreadCrumbs = useMemo(() => {
        if (!fullBreadCrumbs.length)
            return [];
        if (fullBreadCrumbs.length > 1) {
            return fullBreadCrumbs.slice(0, -1);
        }
        return fullBreadCrumbs;
    }, [fullBreadCrumbs]);
    const { siblings, children } = useMemo(() => {
        if (!resolvedSelectedNode)
            return { siblings: [], children: [] };
        return {
            siblings: orgNodes.filter((n) => n.parentId === resolvedSelectedNode.parentId),
            children: orgNodes.filter((n) => n.parentId === resolvedSelectedNode.id),
        };
    }, [orgNodes, resolvedSelectedNode]);
    return (_jsxs("div", { className: "orgSelect-container", children: [_jsx("div", { className: "orgSelect-breadcrumbsWrapper", children: buttonBreadCrumbs.length > 0 && (_jsx(Breadcrumbs, { nodes: buttonBreadCrumbs, onClick: selectNode })) }), _jsx(Button, { className: "orgSelect-currentButton", ref: setAnchorEl, onClick: togglePopover, type: "tertiary", size: "small", text: resolvedSelectedNode ? getNodeDisplayLabel(resolvedSelectedNode) : "", disabled: !resolvedSelectedNode, children: _jsx(SSRIcon, { paths: ChevronDownIcon }) }), isPopoverShown && anchorEl && (_jsx(PopOver, { clickAwayHandler: hidePopover, anchorEl: anchorEl, horizontalAlignment: "left", horizontalPosition: "left", verticalPosition: "bottom", verticalAlignment: "top", onScroll: hidePopover, children: _jsxs("div", { className: "orgSelect-popoverContainer", children: [_jsx("div", { className: "orgSelect-popoverHeader", children: _jsx(Breadcrumbs, { nodes: fullBreadCrumbs, onClick: selectNode }) }), _jsxs("div", { className: "orgSelect-popoverContent", children: [_jsx("div", { className: "orgSelect-list", children: siblings.map((n, idx) => (_jsx(OrgItem, { node: n, isActive: n.id === resolvedSelectedNode?.id, onClick: () => {
                                            selectNode(n.id);
                                        } }, `sibling-${n.type}-${n.id}-${idx}`))) }), _jsx("div", { className: "orgSelect-list orgSelect-list--bordered", children: children.map((n, idx) => (_jsx(OrgItem, { node: n, isActive: n.id === resolvedSelectedNode?.id, onClick: () => {
                                            selectNode(n.id);
                                        } }, `child-${n.type}-${n.id}-${idx}`))) })] })] }) }))] }));
};
//# sourceMappingURL=OrgSelect.js.map